import { useEffect, useRef, useState } from 'react';

type EntryClassNames =
  | {
      beforeEntry?: string;
      entry: string;
    }
  | { entry?: never };

type ExitClassNames =
  | {
      beforeExit?: string;
      exit: string;
    }
  | { exit?: never };

export type ClassNames = EntryClassNames &
  ExitClassNames & {
    out?: string;
    in?: string;
  };

export enum State {
  OUT = 'out',
  WILL_ENTER = 'willEnter',
  ENTERING = 'entering',
  IN = 'in',
  WILL_EXIT = 'willExit',
  EXITING = 'exiting',
}

const useAnimatedMount = <T extends HTMLElement>(
  isOpen: boolean | undefined,
  classNames: ClassNames,
) => {
  const [state, setState] = useState<State>(isOpen ? State.IN : State.OUT);

  const classNameMap = {
    [State.OUT]: classNames.out,
    [State.WILL_ENTER]:
      'beforeEntry' in classNames ? classNames.beforeEntry : undefined,
    [State.ENTERING]: classNames.entry,
    [State.IN]: classNames.in,
    [State.WILL_EXIT]:
      'beforeExit' in classNames ? classNames.beforeExit : undefined,
    [State.EXITING]: classNames.exit,
  };

  const ref = useRef<T>(null);

  useEffect(() => {
    if (isOpen && state !== State.IN) {
      if (classNames.entry) {
        setState(State.WILL_ENTER);

        setTimeout(() => {
          setState(State.ENTERING);

          const listener = (e: TransitionEvent) => {
            if (e.target === ref.current) {
              setState(State.IN);
              ref.current?.removeEventListener('transitionend', listener);
            }
          };

          ref.current?.addEventListener('transitionend', listener);
        }, 10);
      } else {
        setState(State.IN);
      }
    }

    if (!isOpen && state !== State.OUT) {
      if (classNames.exit) {
        setState(State.WILL_EXIT);

        setTimeout(() => {
          setState(State.EXITING);

          const listener = (e: TransitionEvent) => {
            if (e.target === ref.current) {
              setState(State.OUT);
              ref.current?.removeEventListener('transitionend', listener);
            }
          };

          ref.current?.addEventListener('transitionend', listener);
        }, 10);
      } else {
        setState(State.OUT);
      }
    }
  }, [isOpen]);

  const mounted = [
    State.WILL_ENTER,
    State.ENTERING,
    State.IN,
    State.WILL_EXIT,
    State.EXITING,
  ].includes(state);

  return {
    state,
    mounted,
    className: classNameMap[state],
    ref,
  };
};

export default useAnimatedMount;
